const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Generic API fetch helper
 * @param {string} path - API endpoint path
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method
 * @param {Object} options.body - Request body
 * @param {string} options.userId - User ID for authorization
 * @returns {Promise<Object>} - Response data
 */
export async function apiFetch(path, { method = 'GET', body, userId } = {}) {
  const headers = {
    'Content-Type': 'application/json'
  };

  // Add auth header if userId is provided (skip for guest users)
  if (userId && !userId.startsWith('guest:')) {
    headers['Authorization'] = `user:${userId}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  if (!res.ok) {
    let message = 'Erro na API';
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }

  return res.json();
}

/**
 * Get user metadata
 */
export async function getUserMetadata(userId) {
  return apiFetch('/api/user/metadata', {
    method: 'GET',
    userId
  });
}

/**
 * Save or update user metadata
 */
export async function saveUserMetadata(userId, { name, email }) {
  return apiFetch('/api/user/metadata', {
    method: 'POST',
    body: { name, email },
    userId
  });
}

/**
 * Get list of bars with optional filters
 */
export async function getBars(userId = null, filters = {}) {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  const path = `/api/bars${queryString ? `?${queryString}` : ''}`;

  return apiFetch(path, { userId });
}

/**
 * Get bar details by ID
 */
export async function getBarById(barId, userId = null) {
  return apiFetch(`/api/bars/${barId}`, { userId });
}

/**
 * Get bar details by key (slug)
 */
export async function getBarByKey(key, userId = null) {
  return apiFetch(`/api/bars/key/${key}`, { userId });
}

/**
 * Create a new bar suggestion
 */
export async function createBar(userId, barData) {
  return apiFetch('/api/bars', {
    method: 'POST',
    body: barData,
    userId
  });
}

/**
 * Update an existing bar by ID
 */
export async function updateBar(userId, barId, barData) {
  return apiFetch(`/api/bars/${barId}`, {
    method: 'PUT',
    body: barData,
    userId
  });
}

/**
 * Toggle upvote on a bar
 */
export async function toggleUpvote(userId, barId) {
  return apiFetch(`/api/bars/${barId}/upvote`, {
    method: 'POST',
    userId
  });
}

/**
 * Add a comment to a bar
 */
export async function addComment(userId, barId, text, parentId = null) {
  return apiFetch(`/api/bars/${barId}/comments`, {
    method: 'POST',
    body: { text, parentId },
    userId
  });
}